<template>
	<div class="modal-container modal-medium">
		<div class="modal-header">Richtable Settings</div>

		<div
			v-if="rt"
			class="modal-body rt-settings-panels"
		>
			<Panel title="Toggle Table Headers">
				<div :style="$grid('1-1')">
					<button @click="toggleAllHeadersIsVisible(true)">Select All</button>
					<button @click="toggleAllHeadersIsVisible(false)">Unselect All</button>
				</div>

				<div :style="$grid('1-1-1')">
					<InputCheckbox
						v-for="(header, index) in rt.headers"
						:key="'headerList-' + index"
						v-model="header.isVisible"
						:label="header.display ? header.display : '[' + header.name + ']'"
						@change="onRTChange(header, index)"
					>
					</InputCheckbox>
				</div>
			</Panel>

			<Panel
				v-if="downloadAvailable"
				title="Export Current Table"
			>
				<div
					v-if="rt.data.length > 0"
					:style="$grid('1')"
				>
					<div class="export-row column-export-select">
						<InputRichDropdown
							label="Columns to Export"
							:list="columnsAvailableForExport"
							v-model:saturn="exportColumns"
							:valueMap="header => header"
							:display="header => header.display"
							multiSelect
							search
							selectAll
							overflow
						/>

						<span>{{ exportColumns.length }}</span>
					</div>

					<div class="export-row export-rows-filetype">
						<InputNumber
							label="Number of Rows"
							:infoIconObj="{ tooltipText: ['Available: ' + rt.count] }"
							v-model:saturn="exportRowCount"
							:max="rt.count"
							:min="0"
						>
						</InputNumber>

						<InputDropdown
							label="File Type"
							v-model:saturn="exportFileType"
							:list="fileTypeList"
							:text="item => item.display"
							:valueMap="item => item.value"
						>
						</InputDropdown>
					</div>

					<ButtonLoading
						class="button-accept"
						@click="exportTable"
						:disabled="downloadDisabled"
						:isLoading="isBusy"
					>
						Download
					</ButtonLoading>
				</div>

				<div v-else>
					<h3>No data available to export.</h3>
				</div>
			</Panel>
		</div>

		<div
			v-else
			class="modal-body"
			style="color: var(--error-color)"
		>
			<b>Rich Table data not found. No settings to display.</b>
		</div>

		<div class="modal-footer">
			<div v-if="rtPreferences">
				<button
					class="button-unwind"
					@click="cancel()"
				>
					Cancel
				</button>
				<button
					class="button-save"
					@click="close()"
				>
					Save
				</button>
			</div>
			<div v-else>
				<button
					class="button-accept"
					@click="close()"
				>
					Close
				</button>
			</div>
		</div>
	</div>
</template>

<script>
	import api from '@core/services/api/_api';
	import util from '@core/services/util';
	import $modal from '@core/services/modal';
	import Panel from '@core/components/Panel.vue';
	import { RT_FILE_TYPES } from '@core/classes/Enums';
	import ButtonLoading from '@core/components/ButtonLoading.vue';
	import InputNumber from '@core/components/InputNumber.vue';
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputDropdown from '@core/components/InputDropdown.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';

	export default {
		name: 'modalRichTableSettings',
		props: ['modal'],
		data() {
			return {
				isBusy: false,
				rt: null,
				tableDataFiltered: null,
				tableId: null, //Optional, pass in an id if you want selections to be saved to local storage
				rtPreferences: null,
				exportColumns: [],
				exportRowCount: 0,
				exportFileType: null,
				fileTypeList: [
					{ display: 'Comma Separated Values (.csv)', value: RT_FILE_TYPES.CSV },
					{ display: 'Microsoft Excel (.xlsx)', value: RT_FILE_TYPES.EXCEL },
					{ display: 'PDF (.pdf)', value: RT_FILE_TYPES.PDF },
				],
				anyChanges: false,
				downloadRoute: null,
				pdfTitle: null,
			};
		},
		computed: {
			isServerSide() {
				return this.modal.passedData.isServerSide;
			},
			columnsAvailableForExport() {
				let availableColumns = [];

				this.rt.headers.forEach(h => {
					if (h.subHeaders && h.subHeaders.length > 0) availableColumns.push(...h.subHeaders.filter(sh => this.canExportHeader(sh)));
					else if (this.canExportHeader(h)) availableColumns.push(h);
				});

				return availableColumns;
			},
			downloadDisabled() {
				return this.exportColumns.length < 1 || this.exportRowCount < 1 || this.exportFileType === null;
			},
			downloadAvailable() {
				// Serverside must have a passed download route
				return !this.isServerSide || this.downloadRoute;
			},
			activeAutofilters() {
				let selectedHeaders = this.rt.headers.filter(h => h.autoFilter && h.autoFilter.values.some(v => v.isSelected));
				let autoFilters = [];
				selectedHeaders.forEach(h => {
					autoFilters.push({
						columnName: h.display,
						selectedValues: h.autoFilter.values.filter(v => v.isSelected).map(v => v.display),
					});
				});

				return autoFilters;
			},
		},
		async created() {
			this.rt = this.modal.passedData.rt;
			this.tableDataFiltered = this.modal.passedData.tableDataFiltered;
			this.tableId = this.modal.passedData.tableId;
			this.downloadRoute = this.modal.passedData.downloadRoute;
			this.pdfTitle = this.modal.passedData.pdfTitle;

			if (this.tableId) {
				this.rtPreferences = JSON.parse(localStorage.getItem('RichTablePreferences')) ?? [];
			}
		},
		methods: {
			close() {
				//Modify local storage settings
				if (this.rtPreferences && this.anyChanges) {
					let preferenceIndex = this.rtPreferences.findIndex(p => p.tableId === this.tableId);

					if (preferenceIndex != -1) this.rtPreferences.splice(preferenceIndex, 1, this.generatePreferences());
					else this.rtPreferences.push(this.generatePreferences());

					localStorage.setItem('RichTablePreferences', JSON.stringify(this.rtPreferences));
				}

				$modal.close();
			},
			cancel() {
				//Revert back to whatever was saved in local storage
				if (this.rtPreferences && this.anyChanges) {
					let preferenceItem = this.rtPreferences.find(p => p.tableId === this.tableId);

					if (preferenceItem) {
						this.rt.headers.forEach(h => {
							let headerMatch = preferenceItem.headers.find(e => e.name === h.name);
							h.isVisible = headerMatch?.isVisible ?? h.isVisible;
						});
					} else {
						this.rt.headers.forEach(h => (h.isVisible = true));
					}

					this.onRTChange(this.rt.headers[0], 0);
				}

				$modal.cancel();
			},
			toggleAllHeadersIsVisible(isVisible) {
				this.rt.headers.forEach(h => (h.isVisible = isVisible));
				this.onRTChange(this.rt.headers[0], 0);
			},
			onRTChange(header, index) {
				if (!this.anyChanges) this.anyChanges = true;

				//Allows Vue to actually detect the data mutation
				this.rt.headers.splice(index, 1, header);
			},
			generatePreferences() {
				let headers = this.rt.headers.map(h => {
					return { name: h.name, isVisible: h.isVisible };
				});
				return { tableId: this.tableId, headers: headers };
			},
			canExportHeader(header) {
				if (header.dontAllowExport) return false;

				let sampleValue = header.printValue ? header.printValue(this.rt.data[0]) : this.rt.data[0][header.name];
				return !util.isNull(sampleValue) && typeof sampleValue !== 'object';
			},
			async exportTable() {
				this.isBusy = true;

				try {
					if (this.isServerSide) {
						await this.exportTableServerSide();
						return;
					}

					let headerNames = this.exportColumns.map(c => c.display);

					let tableRows = [];
					for (let i = 0; i < this.exportRowCount; i++) {
						let currentRow = this.tableDataFiltered[i];

						let dataCells = [];
						this.exportColumns.forEach(c => {
							let cellValue = c.printValue ? c.printValue(currentRow) : c.value ? c.value(currentRow) : currentRow[c.name];
							dataCells.push(cellValue);
						});

						tableRows.push(dataCells);
					}

					let sortByField = '';
					if (this.rt.sortBy) {
						let sortByColumnDisplay = this.rt.headers.find(h => h.name == this.rt.sortBy)?.display ?? this.rt.sortBy;
						sortByField = `${sortByColumnDisplay} (${this.rt.asc ? 'Ascending' : 'Descending'})`;
					}

					let searchByField = '';
					if (this.rt.searchText) {
						let searchByColumnDisplay = this.rt.headers.find(h => h.name === this.rt.searchBy)?.display ?? this.rt.searchBy;
						searchByField = `${searchByColumnDisplay} - "${this.rt.searchText}"`;
					}

					let downloadRequest = {
						title: this.pdfTitle ? this.pdfTitle : this.tableId ? this.tableId : 'Rich Table',
						headers: headerNames,
						rows: tableRows,
						fileType: this.exportFileType,
						sortBy: sortByField,
						searchBy: searchByField,
						selectedFilters: this.activeAutofilters,
					};

					let fileName = this.getFileName(this.tableId ?? 'RichTable', this.exportFileType);

					await api.utilities.downloadRichTable(downloadRequest, fileName);
				} catch (err) {
					console.error('Error when exporting RichTable: ', err);
					util.toastr('error', 'Error Exporting Table', 'An error occurred when trying to export this table.');
				} finally {
					this.isBusy = false;
				}
			},
			async exportTableServerSide() {
				try {
					let headerNames = this.exportColumns.map(c => c.display);
					let exportHeaders = [];
					this.rt.headers.forEach(h => {
						if (h.subHeaders) exportHeaders.push(...h.subHeaders);
						else exportHeaders.push(h);
					});

					let richTableData = {
						title: this.pdfTitle ? this.pdfTitle : this.tableId ? this.tableId : 'Rich Table',
						headers: headerNames,
						fileType: this.exportFileType,

						pagedDataTable: {
							headers: exportHeaders,
							sortBy: this.rt.sortBy,
							asc: this.rt.asc,
							pageSize: this.exportRowCount,
							pageNumber: 1,
							searchText: this.rt.searchText,
							searchBy: this.rt.searchBy,
						},
					};

					let fileName = this.getFileName(this.tableId ?? 'RichTable', this.exportFileType);

					await this.downloadRoute(richTableData, fileName);
				} catch (err) {
					console.error('Error Exporting Serverside RichTable: ', err);
					util.toastr('error', 'Error Exporting Table', 'An error occurred when trying to export table data.');
				}
			},
			getFileName(tableTitle, fileType) {
				let fileName = tableTitle;
				switch (fileType) {
					case RT_FILE_TYPES.EXCEL:
						fileName += '.xlsx';
						break;
					case RT_FILE_TYPES.PDF:
						fileName += '.pdf';
						break;
					default:
						fileName += '.csv';
				}

				return fileName;
			},
		},
		components: {
			Panel,
			ButtonLoading,
			InputNumber,
			InputCheckbox,
			InputDropdown,
			InputRichDropdown,
		},
	};
</script>

<style scoped>
	.rt-settings-panels {
		display: grid;
		grid-template-columns: 1fr;
		gap: 10px;
	}

	.export-row {
		display: flex;
		align-items: center;
	}

	.export-row > * {
		margin: 0 5px;
	}

	.column-export-select > .InputRichDropdown {
		flex-grow: 1;
	}

	.column-export-select > span {
		padding: 8px 13px;
		border: 1px solid var(--border-color);
		border-radius: 10px;
		background-color: var(--main-color);
		text-align: center;
		color: var(--background-color);
		font-weight: bold;
	}

	.export-rows-filetype {
		align-items: center;
		justify-content: space-evenly;
	}

	.export-rows-filetype > * {
		width: 50%;
	}
</style>
